import {
  Container,
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Radio,
  RadioGroup,
  IconButton,
  Alert,
  CircularProgress,
  Paper,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  Breadcrumbs,
  Link as MuiLink,
  Chip,
  InputAdornment,
  FormHelperText,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowForward,
  ArrowBack as ArrowBackIcon,
  CheckCircle,
  Language as LanguageIcon,
  ExpandMore,
  ExpandLess,
  CloudUpload,
  InfoOutlined,
  EditOutlined,
  AccessTime as AccessTimeIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  VerifiedUser as VerifiedUserIcon,
  PersonOutline as PersonOutlineIcon,
  PhoneIphone as MobileIcon,
  Settings as SettingsIcon,
  ArrowBack 
} from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { adminService } from '../services/admin';
import ApplicationWizard from './ApplicationWizard';
import { emptyDomain } from './ApplicationWizardSteps';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/* ----------------------------------------------------------------------
   EditApplicationPage
   Fetches the existing app + its domain(s) + its settings, shapes them
   into the same `initialValues` object the create wizard uses, then
   hands off to <ApplicationWizard mode="edit" .../> — same steps, same
   look, just pre-filled and wired to the update endpoints instead of the
   create ones.

   Replaces BOTH old EditApplicationPage and EditApplicationPage2 — make
   sure your router only has ONE route left for this path:

     <Route path="applications/:appId/edit" element={<EditApplicationPage />} />

   (Delete the old duplicate — React Router only ever used the first
   match, which is why edits were silently hitting the old page/old API
   calls even after EditApplicationPage2 was fixed.)
---------------------------------------------------------------------- */
const EditApplicationPage = () => {
  const { appId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const listPath = isAdmin ? '/admin/applications' : '/business/applications';

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [initialFormValues, setInitialFormValues] = useState(null);
  const [initialSettingsSaved, setInitialSettingsSaved] = useState(false);
  const [initialVersion, setInitialVersion] = useState(null);
  const [businessStatus, setBusinessStatus] = useState(null);

  useEffect(() => {
    if (appId) loadEverything();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  const loadEverything = async () => {
    setLoading(true);
    setLoadError('');
    try {
      // ── App details ──────────────────────────────────────────────
      const appRes = await adminService.getBusinessApp(appId);
      const app = appRes?.data || appRes;

      // ── Domains ──────────────────────────────────────────────────
      // TODO: confirm shape — the documented "Get Domain with AppID"
      // endpoint only takes an appId (no domain name), so it's unclear
      // whether it returns ONE domain or an ARRAY of all domains for
      // this app. This normalizes either shape into an array so the
      // wizard's domains list works either way — verify against a real
      // response and simplify once confirmed.
      let domainsList = [];
      if (app.appType?.toLowerCase() !== 'mobile') {
        try {
          const domainRes = await adminService.getAppDomain(appId);
          const raw = domainRes?.data || domainRes;
          if (Array.isArray(raw)) domainsList = raw;
          else if (raw && Object.keys(raw).length) domainsList = [raw];
        } catch {
          // no domains saved yet — leave list empty
        }
      }

      const mappedDomains = domainsList.map((d) => ({
        id: true,
        savedDomainName: d.domainName,
        domain: d.domainName || '',
        displayName: d.displayName || '',
        redirectUrl: d.redirectUrl || 'https://',
        status: d.enabled !== false,
        saved: true,
        logoUrl: d.logoUrl || '',
        _tempId: Date.now() + Math.random(),
      }));

      // ── Settings ─────────────────────────────────────────────────
      // If this throws (e.g. 404 because settings were never saved for
      // this app), fall back to defaults and let the wizard POST them
      // fresh on save, same as create mode would.
      let settings = null;
      try {
        const settingsRes = await adminService.getAppSettings(appId);
        settings = settingsRes?.data || settingsRes;
      } catch {
        settings = null;
      }

      const parsedBlockDuration = settings?.blockDuration
        ? parseInt(String(settings.blockDuration), 10) || 30
        : 30;

      setInitialFormValues({
        businessId: app.businessId || '',
        applicationType: (app.appType || 'web').toUpperCase(),
        description: app.appDescription || '',
        businessName: app.appName || '',
        isActive: app.enabled !== false,
        domains: mappedDomains.length ? mappedDomains : [emptyDomain()],
        blockDuration: parsedBlockDuration,
        failedAttempts: settings?.failedAttemptsBeforeBlock || 5,
        ipGeolocationEnabled: !!settings?.ipGeolocationEnabled,
        webhookUrl: settings?.webhookUrl || '',
        metadataJson: JSON.stringify(
          settings?.metadata && Object.keys(settings.metadata).length ? settings.metadata : { placeholder: true },
          null,
          2
        ),
        appLogoUrl: app.appLogoUrl || app.blockDetails?.appLogoUrl || settings?.appLogoUrl || '',
      });
      setInitialSettingsSaved(!!settings);
      setBusinessStatus(app.businessStatus || null);
      setInitialVersion(app.updatedAt ? String(app.updatedAt) : null);
    } catch (err) {
      console.error('Error loading application for edit:', err);
      setLoadError(err.message || 'Failed to load application details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError || !initialFormValues) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(listPath)} sx={{ mb: 2 }}>
          Back to Applications
        </Button>
        <Alert severity="error">{loadError || 'Application not found.'}</Alert>
      </Box>
    );
  }

  return (
    <ApplicationWizard
      mode="edit"
      initialAppId={appId}
      initialFormValues={initialFormValues}
      initialSettingsSaved={initialSettingsSaved}
      initialVersion={initialVersion}
      initialBusinessStatus={businessStatus}
      onDone={() => navigate(listPath)}
    />
  );
};

export default EditApplicationPage;
